import { useState, useCallback, useRef, useEffect } from "react";
import {
  createConversation,
  createChatStreamWithFetch,
  uploadContextFile,
} from "views/admin/chatbot/services";
import { ChatMessage, ChatState } from "views/admin/chatbot/chatbot";

export const useChatbot = () => {
  const [state, setState] = useState<ChatState>({
    conversationId: null,
    messages: [],
    isLoading: false,
    isStreaming: false,
    currentStreamingMessage: "",
    error: null,
    isUploadingFile: false,
  });

  const [pendingFiles, setPendingFiles] = useState<File[]>([]);

  const readerRef = useRef<ReadableStreamDefaultReader<Uint8Array> | null>(
    null
  );
  const currentMessageIdRef = useRef<number>(0);

  const cleanup = useCallback(() => {
    if (readerRef.current) {
      readerRef.current.cancel();
      readerRef.current = null;
    }
  }, []);

  const addPendingFile = useCallback((file: File) => {
    setPendingFiles((prev) => [...prev, file]);
  }, []);

  const removePendingFile = useCallback((fileName: string) => {
    setPendingFiles((prev) => prev.filter((f) => f.name !== fileName));
  }, []);

  const clearPendingFiles = useCallback(() => {
    setPendingFiles([]);
  }, []);

  const uploadPendingFiles = useCallback(async (): Promise<string[]> => {
    if (pendingFiles.length === 0) return [];

    try {
      setState((prev) => ({
        ...prev,
        isUploadingFile: true,
        error: null,
      }));

      const uploadPromises = pendingFiles.map((file) =>
        uploadContextFile(file)
      );
      const uploadResults = await Promise.all(uploadPromises);

      setState((prev) => ({
        ...prev,
        isUploadingFile: false,
      }));

      setPendingFiles([]);

      return uploadResults.map((result) => result.fileId);
    } catch (error) {
      setState((prev) => ({
        ...prev,
        error: "Failed to upload files",
        isUploadingFile: false,
      }));
      throw error;
    }
  }, [pendingFiles]);

  const createConversationHandler = useCallback(async () => {
    try {
      setState((prev) => ({ ...prev, isLoading: true, error: null }));
      const response = await createConversation();
      setState((prev) => ({
        ...prev,
        conversationId: response.id,
        messages: [],
        isLoading: false,
      }));
      return response.id;
    } catch (error) {
      setState((prev) => ({
        ...prev,
        error: "Failed to create conversation",
        isLoading: false,
      }));
      throw error;
    }
  }, []);

  const parseSSEData = (
    chunk: string
  ): { event: string; data: string } | null => {
    const lines = chunk.split("\n");
    let event = "";
    let data = "";

    for (const line of lines) {
      if (line.startsWith("event:")) {
        event = line.substring(6).trim();
      } else if (line.startsWith("data:")) {
        data += line.substring(5) + "\n";
      }
    }

    if (event && data) {
      return { event, data: data.trimEnd() };
    }
    return null;
  };

  const sendMessage = useCallback(
    async (message: string) => {
      let currentConversationId = state.conversationId;
      if (!currentConversationId || state.isStreaming) {
        currentConversationId = await createConversationHandler();
      }

      try {
        let fileIds: string[] = [];
        if (pendingFiles.length > 0) {
          fileIds = await uploadPendingFiles();

          const fileNames = pendingFiles.map((file) => file.name).join(", ");
          const fileMessage = `${fileNames} sent`;

          const fileConfirmationMessage: ChatMessage = {
            id: ++currentMessageIdRef.current,
            message: fileMessage,
            by: "user",
            timestamp: new Date(),
          };

          setState((prev) => ({
            ...prev,
            messages: [...prev.messages, fileConfirmationMessage],
          }));
        }

        const userMessage: ChatMessage = {
          id: ++currentMessageIdRef.current,
          message,
          by: "user",
          timestamp: new Date(),
        };

        setState((prev) => ({
          ...prev,
          messages: [...prev.messages, userMessage],
          isStreaming: true,
          currentStreamingMessage: "",
          error: null,
        }));

        cleanup();

        const reader = await createChatStreamWithFetch(
          currentConversationId,
          message,
          fileIds.length > 0 ? fileIds : undefined
        );
        readerRef.current = reader;

        let streamingMessage = "";
        const responseMessageId = ++currentMessageIdRef.current;
        const decoder = new TextDecoder();

        const readChunk = async (): Promise<void> => {
          try {
            const { done, value } = await reader.read();

            if (done) {
              if (streamingMessage) {
                const modelMessage: ChatMessage = {
                  id: responseMessageId,
                  message: streamingMessage,
                  by: "model",
                  timestamp: new Date(),
                };

                setState((prev) => ({
                  ...prev,
                  messages: [...prev.messages, modelMessage],
                  isStreaming: false,
                  currentStreamingMessage: "",
                }));
              } else {
                setState((prev) => ({
                  ...prev,
                  isStreaming: false,
                  currentStreamingMessage: "",
                }));
              }
              return;
            }

            const chunk = decoder.decode(value, { stream: true });
            const sseData = parseSSEData(chunk);

            if (sseData && sseData.event === "response") {
              streamingMessage += sseData.data;
              setState((prev) => ({
                ...prev,
                currentStreamingMessage: streamingMessage,
              }));
            }

            await readChunk();
          } catch (error) {
            setState((prev) => ({
              ...prev,
              error: "Failed to send message",
              isStreaming: false,
              currentStreamingMessage: "",
            }));
            cleanup();
          }
        };

        await readChunk();
      } catch (error) {
        setState((prev) => ({
          ...prev,
          error: "Failed to send message",
          isStreaming: false,
          currentStreamingMessage: "",
        }));
        cleanup();
      }
    },
    [
      state.conversationId,
      state.isStreaming,
      pendingFiles,
      createConversationHandler,
      cleanup,
      uploadPendingFiles,
    ]
  );

  const startNewConversation = useCallback(async () => {
    cleanup();
    setPendingFiles([]);
    setState({
      conversationId: null,
      messages: [],
      isLoading: false,
      isStreaming: false,
      currentStreamingMessage: "",
      error: null,
      isUploadingFile: false,
    });
    return await createConversationHandler();
  }, [createConversationHandler, cleanup]);

  useEffect(() => {
    return cleanup;
  }, [cleanup]);

  return {
    ...state,
    pendingFiles,
    createConversation: createConversationHandler,
    sendMessage,
    startNewConversation,
    cleanup,
    addPendingFile,
    removePendingFile,
    clearPendingFiles,
  };
};
